import { useCallback, useState } from 'react';
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@reflex/ui/components/ui/sheet';
import { Button } from '@reflex/ui/components/ui/button';
import { FileText } from 'lucide-react';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { api } from '@/lib/api';
import { ResizableSheetContent } from '@/components/sidebar/ResizableSheetContent';
import { LogViewer } from '@reflex/ui/components/content/log-viewer';

const DEVBOX_LOG_PATH = 'runloop://devbox/logs';

export function ViewLogsButton({ agentId }: { agentId: string }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.getDevboxLogs(agentId);
      setContent(next || '(no devbox logs)');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch devbox logs');
      setContent(null);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen);
    if (isOpen && content === null && !loading) {
      void fetchLogs();
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" data-testid="agent-detail-view-logs">
          <FileText className="h-3 w-3" />
          View logs
        </Button>
      </SheetTrigger>
      <ResizableSheetContent
        defaultWidth={600}
        minWidth={400}
        maxWidth={1200}
        storageKey="debug-logs-panel-width"
        className="flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-2 shrink-0">
          <SheetTitle className="text-base">Devbox platform logs</SheetTitle>
          <SheetDescription className="text-xs">
            Runloop-emitted logs for the agent&apos;s devbox (startup, exec stderr, snapshot
            activity).
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 min-h-0 px-6 pb-6 flex flex-col">
          {loading ? (
            <PageDescription className="mt-0 py-4">Loading&hellip;</PageDescription>
          ) : error ? (
            <PageDescription className="mt-0 py-4 text-sm text-destructive">
              {error}
            </PageDescription>
          ) : content !== null ? (
            <div className="flex-1 min-h-0">
              <LogViewer content={content} path={DEVBOX_LOG_PATH} />
            </div>
          ) : null}
          {content !== null && !loading && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 shrink-0"
              onClick={() => void fetchLogs()}
            >
              Refresh
            </Button>
          )}
        </div>
      </ResizableSheetContent>
    </Sheet>
  );
}
